export type MusicState =
  | "menu"
  | "combat"
  | "elite"
  | "boss"
  | "victory"
  | "defeat";

export const MUSIC_TRACKS: Readonly<Record<MusicState, string>> = {
  menu: "/assets/generated/audio/menu.wav",
  combat: "/assets/generated/audio/combat.wav",
  elite: "/assets/generated/audio/elite.wav",
  boss: "/assets/generated/audio/boss.wav",
  victory: "/assets/generated/audio/victory.wav",
  defeat: "/assets/generated/audio/defeat.wav",
};

export class MusicDirector {
  #audio: HTMLAudioElement | null = null;
  #state: MusicState | null = null;
  #unlocked = false;
  #volume = 0;

  constructor(volumePercent = 70) {
    this.setVolume(volumePercent);
  }

  get state(): MusicState | null {
    return this.#state;
  }

  setVolume(volumePercent: number): void {
    const wasMuted = this.#volume <= 0;
    this.#volume = Math.max(0, Math.min(1, volumePercent / 100)) * 0.32;
    if (!this.#audio) return;
    this.#audio.volume = this.#volume;
    if (this.#volume <= 0) {
      this.#audio.pause();
    } else if (wasMuted && this.#unlocked && this.#state) {
      void this.#audio.play().catch(() => undefined);
    }
  }

  setState(state: MusicState): void {
    if (this.#state === state) return;
    this.#state = state;
    const audio = this.#audio;
    if (!audio) return;
    audio.pause();
    audio.src = MUSIC_TRACKS[state];
    audio.loop = state !== "victory" && state !== "defeat";
    audio.currentTime = 0;
    if (this.#unlocked && this.#volume > 0) {
      void audio.play().catch(() => undefined);
    }
  }

  unlock(): void {
    this.#unlocked = true;
    if (!this.#audio && typeof Audio !== "undefined") {
      this.#audio = new Audio();
      this.#audio.preload = "auto";
      this.#audio.volume = this.#volume;
      if (this.#state) {
        this.#audio.src = MUSIC_TRACKS[this.#state];
        this.#audio.loop = this.#state !== "victory" && this.#state !== "defeat";
      }
    }
    if (this.#audio && this.#state && this.#volume > 0) {
      void this.#audio.play().catch(() => undefined);
    }
  }
}

export function musicStateForBattle(
  state: Pick<BattleState, "phase" | "enemies">,
): MusicState | null {
  if (state.phase === "victory") return "victory";
  if (state.phase === "gameover") return "defeat";
  if (state.phase === "paused") return null;
  if (state.phase !== "combat") return "menu";
  const ranks = state.enemies.map((enemy) => ENEMY_BY_ID.get(enemy.kind)?.rank);
  if (ranks.includes("boss")) return "boss";
  if (ranks.includes("elite")) return "elite";
  return "combat";
}

export const soundtrack = new MusicDirector();
import { ENEMY_BY_ID } from "../core/content";
import type { BattleState } from "../core/types";
